import classNames from "classnames";
import { ElementType, ReactNode } from "react";

/**
 * v2 typography primitive. Centralises the Inter-only type ramp used
 * across the new homepage so individual sections stop sprinkling
 * `font-inter` / `font-sans` / inline `letter-spacing` / `line-height`
 * overrides. Every piece of copy on HomeV2 should render through this.
 *
 * Variants match graphy.com (measured from prod):
 *   - eyebrow      12px medium uppercase tracking 0.6px     — SectionHeader eyebrow
 *   - display-lg   48px medium tracking -0.02em              — H1/H2 (lg breakpoint)
 *   - display-md   40px medium tracking -0.02em              — H1/H2 (md breakpoint)
 *   - display-sm   32px medium tracking -0.02em              — H1/H2 (mobile)
 *   - title-xl     28px medium tracking -0.02em              — stats numbers
 *   - title-lg     24px medium tracking -0.015em             — card titles
 *   - title-md     20px semibold                             — accordion / sub-cards
 *   - title-sm     18px semibold                             — small card titles
 *   - body-lg      16px medium                               — subtitles
 *   - body-md      14px regular                              — card descriptions
 *   - body-sm      13px regular                              — footnotes
 *   - label        12px semibold uppercase                   — misc chips
 */
export type TextVariant =
  | "eyebrow"
  | "display-lg"
  | "display-md"
  | "display-sm"
  | "title-xl"
  | "title-lg"
  | "title-md"
  | "title-sm"
  | "body-lg"
  | "body-md"
  | "body-sm"
  | "label";

// All heading variants (display-* / title-*) use a consistent -1.5px
// letter-spacing to match the Inter display tracking spec for the new
// home page. Body and label variants keep their natural spacing.
const variantClass: Record<TextVariant, string> = {
  eyebrow: "font-inter text-[12px] font-medium uppercase",
  "display-lg":
    "font-inter font-medium leading-[1.15] tracking-[-1px] text-[24px] md:leading-[1.1] md:tracking-[-1.5px] md:text-[48px]",
  "display-md":
    "font-inter font-medium leading-[1.15] tracking-[-1px] text-[24px] md:leading-[1.1] md:tracking-[-1.5px] md:text-[40px]",
  "display-sm":
    "font-inter font-medium leading-[1.15] tracking-[-1px] text-[24px] md:leading-[1.1] md:tracking-[-1.5px] md:text-[32px]",
  "title-xl":
    "font-inter font-medium tracking-[-0.6px] text-[28px] md:text-[32px] leading-none",
  "title-lg":
    "font-inter font-medium leading-tight tracking-[-0.4px] text-[22px] md:text-[24px]",
  "title-md":
    "font-inter font-semibold text-[20px] leading-tight tracking-[-0.3px]",
  "title-sm":
    "font-inter font-semibold text-[18px] leading-tight tracking-[-0.2px]",
  // Subtext (hero description, section subtitles, card descriptions,
  // footnotes) all render in Inter Regular (400) for a calmer read
  // beneath the Inter Medium headings.
  "body-lg": "font-inter font-normal text-[16px] leading-[24px]",
  "body-md": "font-inter font-normal text-[14px] leading-[20px]",
  "body-sm": "font-inter font-normal text-[13px] leading-[18px]",
  label: "font-inter text-[12px] font-semibold uppercase tracking-wide",
};

export type TextProps = {
  as?: ElementType;
  variant: TextVariant;
  className?: string;
  style?: React.CSSProperties;
  children: ReactNode;
};

const Text = ({
  as: Component = "span",
  variant,
  className,
  style,
  children,
}: TextProps) => {
  return (
    <Component
      className={classNames(variantClass[variant], className)}
      style={style}
    >
      {children}
    </Component>
  );
};

export default Text;
